import { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { describeDateValue } from './formats';

interface RelativeDateInputProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  /**
   * Visual size for the trigger. `sm` matches the column-filter popover.
   * `default` matches the conditional-format dialog.
   */
  size?: 'default' | 'sm';
}

const PRESETS = [
  { token: '@today', label: 'Today' },
  { token: '@start-of-week', label: 'Start of this week' },
  { token: '@start-of-month', label: 'Start of this month' },
  { token: '@start-of-year', label: 'Start of this year' },
];

export function RelativeDateInput({
  value,
  onChange,
  placeholder = 'Pick a date',
  size = 'default',
}: RelativeDateInputProps) {
  const [open, setOpen] = useState(false);
  const display = value ? describeDateValue(value) : '';

  const isRelative = value.startsWith('@');
  const isDaysAgo = value.startsWith('@days-ago:');
  const isDaysFromNow = value.startsWith('@days-from-now:');
  const absoluteDate = !isRelative && value ? toDateInputValue(value) : '';

  const daysCount = isDaysAgo
    ? value.replace('@days-ago:', '')
    : isDaysFromNow
      ? value.replace('@days-from-now:', '')
      : '';

  const choose = (token: string) => {
    onChange(token);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center justify-between gap-2 w-full px-2.5 rounded-md border border-slate-200 bg-white text-left',
            size === 'sm' ? 'h-8 text-xs' : 'h-9 text-sm',
            display ? 'text-slate-900' : 'text-slate-400'
          )}
        >
          <span className="flex items-center gap-1.5 min-w-0">
            <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="truncate">{display || placeholder}</span>
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-72 p-2 space-y-2"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div>
          <Label>Specific date</Label>
          <Input
            type="date"
            value={absoluteDate}
            onChange={(e) => onChange(e.target.value)}
            className="h-8 text-xs"
          />
        </div>

        <div className="grid grid-cols-2 gap-1">
          {PRESETS.map((p) => (
            <button
              key={p.token}
              type="button"
              onClick={() => choose(p.token)}
              className={cn(
                'px-2 py-1.5 rounded-md text-xs text-left border',
                value === p.token
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              )}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div>
          <Label>Relative</Label>
          <div className="flex items-center gap-1.5">
            <Input
              type="number"
              min="0"
              value={isDaysAgo || isDaysFromNow ? daysCount : ''}
              onChange={(e) => {
                const n = e.target.value;
                if (!n) {
                  onChange('');
                  return;
                }
                onChange(isDaysFromNow ? `@days-from-now:${n}` : `@days-ago:${n}`);
              }}
              placeholder="N"
              className="h-8 text-xs w-16"
            />
            <span className="text-xs text-slate-500">days</span>
            <select
              value={isDaysFromNow ? 'fwd' : 'ago'}
              onChange={(e) => {
                if (!daysCount) return;
                onChange(e.target.value === 'fwd' ? `@days-from-now:${daysCount}` : `@days-ago:${daysCount}`);
              }}
              className="h-8 text-xs rounded-md border border-slate-200 bg-white px-1.5"
            >
              <option value="ago">ago</option>
              <option value="fwd">from now</option>
            </select>
          </div>
        </div>

        {value && (
          <button
            type="button"
            onClick={() => {
              onChange('');
              setOpen(false);
            }}
            className="w-full px-2 py-1 text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-50 rounded-md text-center"
          >
            Clear
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1">
      {children}
    </div>
  );
}

function toDateInputValue(raw: string): string {
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parts = raw.split('/').map(Number);
  if (parts.length === 3 && !parts.some(Number.isNaN)) {
    const [m, d, y] = parts;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  return '';
}
